import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { Text, Button, makeStyles, tokens } from '@fluentui/react-components';
import { Dismiss24Regular, ChevronLeft24Regular } from '@fluentui/react-icons';

interface HelpPanelProps {
  open: boolean;
  onClose: () => void;
}

const PAGES = [
  { id: 'getting-started', title: 'Getting Started' },
  { id: 'register', title: 'The Register' },
  { id: 'insights', title: 'Insights' },
  { id: 'import-export', title: 'Import & Export' },
  { id: 'accounts', title: 'Accounts' },
];

const useStyles = makeStyles({
  overlay: {
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    backgroundColor: 'rgba(0,0,0,0.4)',
    zIndex: 2000,
  },
  panel: {
    position: 'fixed',
    top: '0',
    right: '0',
    width: '520px',
    maxWidth: '100vw',
    height: '100vh',
    backgroundColor: '#fff',
    zIndex: 2001,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
    '@media (max-width: 767px)': {
      width: '100vw',
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalM,
    borderBottom: '1px solid #e5e7eb',
    backgroundColor: '#f9fafb',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  content: {
    flex: '1',
    overflowY: 'auto',
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalXXL,
    paddingLeft: tokens.spacingHorizontalXL,
    paddingRight: tokens.spacingHorizontalXL,
  },
  tocList: {
    listStyle: 'none',
    paddingLeft: '0',
    marginTop: tokens.spacingVerticalL,
  },
  tocItem: {
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: tokens.borderRadiusMedium,
    color: '#1a1a2e',
  },
  markdown: {
    '& img': {
      maxWidth: '100%',
      height: 'auto',
      borderRadius: '8px',
      border: '1px solid #eee',
      marginTop: '12px',
      marginBottom: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    },
    '& h1': {
      fontSize: '24px',
      fontWeight: 700,
      marginBottom: '16px',
      color: '#1a1a2e',
    },
    '& h2': {
      fontSize: '20px',
      fontWeight: 600,
      marginTop: '32px',
      marginBottom: '12px',
      color: '#1a1a2e',
    },
    '& h3': {
      fontSize: '16px',
      fontWeight: 600,
      marginTop: '24px',
      marginBottom: '8px',
    },
    '& p': {
      fontSize: '14px',
      lineHeight: '1.7',
      color: '#1a1a2e',
      marginBottom: '12px',
    },
    '& ul, & ol': {
      paddingLeft: '24px',
      marginBottom: '12px',
    },
    '& li': {
      fontSize: '14px',
      lineHeight: '1.7',
      color: '#1a1a2e',
      marginBottom: '4px',
    },
    '& code': {
      backgroundColor: '#f3f4f6',
      padding: '2px 6px',
      borderRadius: '4px',
      fontSize: '13px',
      fontFamily: "'SF Mono', 'Consolas', monospace",
    },
    '& pre': {
      backgroundColor: '#f3f4f6',
      padding: '16px',
      borderRadius: '8px',
      overflow: 'auto',
      marginBottom: '16px',
      color: '#1a1a2e',
    },
    '& pre code': {
      backgroundColor: 'transparent',
      padding: '0',
      color: '#1a1a2e',
    },
    '& table': {
      width: '100%',
      borderCollapse: 'collapse',
      marginBottom: '16px',
      fontSize: '13px',
    },
    '& th, & td': {
      padding: '8px 12px',
      borderBottom: '1px solid #e5e7eb',
      textAlign: 'left',
    },
    '& th': {
      fontWeight: 600,
      backgroundColor: '#f9fafb',
    },
    '& strong': {
      fontWeight: 600,
    },
    '& a': {
      color: '#2c5282',
      textDecoration: 'none',
    },
  },
});

export const HelpPanel: React.FC<HelpPanelProps> = ({ open, onClose }) => {
  const styles = useStyles();
  const [currentPage, setCurrentPage] = useState<string | null>(null);
  const [markdown, setMarkdown] = useState('');
  const [loading, setLoading] = useState(false);

  const loadPage = useCallback(async (pageId: string) => {
    setLoading(true);
    try {
      const res = await fetch(`/docs/${pageId}.md`);
      if (res.ok) {
        const text = await res.text();
        setMarkdown(text);
        setCurrentPage(pageId);
      }
    } catch (err) {
      console.error('Failed to load help page:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && !currentPage) {
      setCurrentPage(null);
      setMarkdown('');
    }
  }, [open, currentPage]);

  if (!open) return null;

  const currentTitle = PAGES.find(p => p.id === currentPage)?.title || 'Help';

  return (
    <>
      <div className={styles.overlay} onClick={onClose} />
      <div className={styles.panel}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            {currentPage && (
              <Button
                appearance="subtle"
                icon={<ChevronLeft24Regular />}
                onClick={() => { setCurrentPage(null); setMarkdown(''); }}
                size="small"
              />
            )}
            <Text size={400} weight="semibold" style={{ color: '#1a1a2e' }}>
              {currentPage ? currentTitle : 'Help & Guide'}
            </Text>
          </div>
          <Button
            appearance="subtle"
            icon={<Dismiss24Regular />}
            onClick={onClose}
          />
        </div>
        <div className={styles.content}>
          {!currentPage ? (
            <>
              <Text size={300} style={{ color: '#6b7280' }}>
                Learn how to use BalanceScript to manage your finances.
              </Text>
              <ul className={styles.tocList}>
                {PAGES.map(page => (
                  <li
                    key={page.id}
                    className={styles.tocItem}
                    onClick={() => loadPage(page.id)}
                  >
                    <Text size={300} weight="semibold" style={{ color: '#1a1a2e' }}>{page.title}</Text>
                    <Text size={200} style={{ color: '#9ca3af' }}>→</Text>
                  </li>
                ))}
              </ul>
            </>
          ) : loading ? (
            <Text>Loading...</Text>
          ) : (
            <div className={styles.markdown}>
              <ReactMarkdown
                components={{
                  a: ({ href, children }) => {
                    const pageId = PAGES.find(p => p.id === href)?.id;
                    if (pageId) {
                      return (
                        <a
                          href="#"
                          onClick={(e) => { e.preventDefault(); loadPage(pageId); }}
                          style={{ color: '#2c5282', cursor: 'pointer', textDecoration: 'none', fontWeight: 600 }}
                        >
                          {children}
                        </a>
                      );
                    }
                    return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
                  },
                }}
              >
                {markdown}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
